import React, { useState, useEffect } from 'react';
import { ref, onValue, get, update, push } from 'firebase/database';
import { db, auth } from '../firebase/config';
import { FaWallet, FaCoins, FaLock, FaShoppingCart, FaTag, FaInfoCircle } from 'react-icons/fa';

const OfferWallet = () => {
  const [balance, setBalance] = useState(0);
  const [offerProducts, setOfferProducts] = useState([]);
  const [purchasedProducts, setPurchasedProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);

  useEffect(() => {
    const currentUser = auth.currentUser;
    if (currentUser) {
      setUser(currentUser);
    }
  }, []);

  useEffect(() => {
    if (!user?.uid) return;

    const offerWalletRef = ref(db, `HTAMS/users/${user.uid}/OfferWallet`);
    const unsubWallet = onValue(offerWalletRef, (snapshot) => {
      const walletBalance = snapshot.val() || 0;
      setBalance(walletBalance);
    });

    const purchasedRef = ref(db, `HTAMS/users/${user.uid}/purchasedOfferProducts`);
    const unsubPurchased = onValue(purchasedRef, (snapshot) => {
      if (snapshot.exists()) {
        const purchased = Object.values(snapshot.val());
        setPurchasedProducts(purchased);
      } else {
        setPurchasedProducts([]);
      }
    });

    const fetchOfferProducts = async () => {
      try {
        const productsRef = ref(db, 'HTAMS/company/offerproduct');
        const snapshot = await get(productsRef);

        if (snapshot.exists()) {
          const products = Object.entries(snapshot.val())
            .map(([id, data]) => ({ id, ...data }))
            .filter(product => product.active)
            .sort((a, b) => (a.offerPrice || 0) - (b.offerPrice || 0));

          setOfferProducts(products);
        }
      } catch (error) {
        console.error('Error fetching offer products:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchOfferProducts();

    return () => {
      unsubWallet();
      unsubPurchased();
    };
  }, [user]);

  const handleBuyNow = async (product) => {
    if (balance < product.offerPrice) {
      alert('❌ Insufficient balance in Offer Wallet!');
      return;
    }

    const productName = product.name || product.productName || 'this product';
    if (!window.confirm(`Buy ${productName} for ₹${product.offerPrice}?`)) return;

    try {
      const newBalance = balance - product.offerPrice;

      const purchaseData = {
        productId: product.id,
        productName: productName,
        price: product.offerPrice,
        purchasedAt: Date.now()
      };

      await update(ref(db, `HTAMS/users/${user.uid}`), {
        OfferWallet: newBalance
      });

      await push(ref(db, `HTAMS/users/${user.uid}/purchasedOfferProducts`), purchaseData);

      alert(`✅ Successfully purchased ${productName}!`);
    } catch (error) {
      console.error('Error purchasing product:', error);
      alert('❌ Failed to purchase product');
    }
  };

  const canBuyProduct = (product, index) => {
    if (balance < product.offerPrice) return false;
    if (index === 0) return true;

    const previousProduct = offerProducts[index - 1];
    return purchasedProducts.some(p => p.productId === previousProduct.id);
  };

  const isProductPurchased = (productId) => {
    return purchasedProducts.some(p => p.productId === productId);
  };

  const getNextAvailableProduct = () => {
    for (let i = 0; i < offerProducts.length; i++) {
      if (!isProductPurchased(offerProducts[i].id)) {
        return offerProducts[i];
      }
    }
    return null;
  };

  if (loading) {
    return (
      <div className="offer-wallet-container">
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  const nextProduct = getNextAvailableProduct();

  return (
    <div className="offer-wallet-container">
      <div className="page-header">
        <div className="header-left">
          <FaWallet className="header-icon" />
          <div>
            <h1>Offer Wallet</h1>
            <p>Sequential purchase system</p>
          </div>
        </div>
        <div className="balance-mini-card">
          <FaCoins className="balance-icon-mini" />
          <div>
            <span className="balance-label-mini">Balance</span>
            <h3 className="balance-amount-mini">{balance.toLocaleString()}</h3>
            <h5>Offer Points</h5>
          </div>
        </div>
      </div>

      {nextProduct && (
        <div className="next-product-banner">
          <div className="banner-content">
            <FaTag className="banner-icon" />
            <div>
              <h4>Next Available offer</h4>
              {/* <p>{nextProduct.name || 'Product'} - Worth ₹ {nextProduct.offerPrice.toLocaleString()}</p> */}
            </div>
          </div>
          {balance >= nextProduct.offerPrice && (
            <button className="btn-quick-buy" onClick={() => handleBuyNow(nextProduct)}>
              <FaShoppingCart /> Buy Now
            </button>
          )}
        </div>
      )}

      <div className="products-section">
        <h3 className="section-title">
          <FaTag /> Offer Products (Buy in Order)
        </h3>

        {offerProducts.filter(p => !isProductPurchased(p.id)).length === 0 ? (
          <div className="empty-state">
            <FaTag className="empty-icon" />
            <p>All products purchased! 🎉</p>
          </div>
        ) : (
          <div className="products-grid">
            {offerProducts
              .filter((product) => !isProductPurchased(product.id))
              .map((product, displayIndex) => {
                const originalIndex = offerProducts.findIndex(p => p.id === product.id);
                const canBuy = canBuyProduct(product, originalIndex);
                const isLocked = !canBuy;

                return (
                  <div
                    key={product.id}
                    className={`product-card ${isLocked ? 'locked' : ''}`}
                  >
                    <div className="product-image">
                      {product.imageUrl ? (
                        <img src={product.imageUrl} alt={product.name || 'Product'} />
                      ) : (
                        <div className="image-placeholder">
                          <FaTag />
                        </div>
                      )}
                      <div className="discount-badge">{product.discount || 0}% OFF</div>
                      {product.label && (
                        <div className="label-badge">
                          <FaTag /> {product.label}
                        </div>
                      )}
                      <div className="step-badge">Step {displayIndex + 1}</div>
                      {isLocked && (
                        <div className="locked-overlay">
                          <FaLock />
                          <span>Locked</span>
                        </div>
                      )}
                    </div>

                    <div className="product-info">
                      <h4 className="product-name">{product.name || product.productName || 'Unnamed Product'}</h4>
                      <div className="price-row">
                        <span className="offer-price">₹ {product.offerPrice?.toLocaleString()}</span>
                        <span className="original-price">Worth ₹  {product.originalPrice?.toLocaleString()}</span>
                      </div>
                      {isLocked && originalIndex > 0 && (
                        <div className="lock-message">
                          Claim  ₹{offerProducts[originalIndex - 1].offerPrice} first
                        </div>
                      )}
                    </div>

                    <div className="product-actions">
                      {canBuy ? (
                        <button
                          className="btn-buy-card"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleBuyNow(product);
                          }}
                        >
                          <FaShoppingCart /> Buy Now
                        </button>
                      ) : (
                        <button className="btn-locked-card" disabled>
                          <FaLock /> Locked
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
          </div>
        )}
      </div>

      <div className="instructions-section">
        <h3 className="section-title">
          <FaInfoCircle /> How Sequential Purchase Works
        </h3>
        <div className="instructions-grid">
          <div className="instruction-card">
            <div className="instruction-number">1</div>
            <div className="instruction-content">
              <h4> Start to claim in sequence</h4>
              <p>You must claim offers in sequence starting from first to last</p>
            </div>
          </div>
          <div className="instruction-card">
            <div className="instruction-number">2</div>
            <div className="instruction-content">
              <h4> Unlock Next Offer</h4>
              <p>After claiming, the next offer in sequence becomes available</p>
            </div>
          </div>
          <div className="instruction-card">
            <div className="instruction-number">3</div>
            <div className="instruction-content">
              <h4>Check Your Balance</h4>
              <p>Ensure you have sufficient balance of offer points before attempting to claim</p>
            </div>
          </div>
          <div className="instruction-card">
            <div className="instruction-number">4</div>
            <div className="instruction-content">
              <h4>Offer disappear After Claim</h4>
              <p>Once claimed, offers are removed from the list automatically.</p>
            </div>
          </div>
        </div>
      </div>

      <style jsx>{`
        .offer-wallet-container {
          padding: 4px;
          background: var(--mobile-bg-page);
          min-height: 100vh;
        }

        .loading-state {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          min-height: 60vh;
          gap: 16px;
        }

        .spinner {
          width: 40px;
          height: 40px;
          border: 4px solid var(--mobile-border-light);
          border-top-color: var(--mobile-primary);
          border-radius: 50%;
          animation: spin 1s linear infinite;
        }

        @keyframes spin {
          to { transform: rotate(360deg); }
        }

        .page-header {
          background: var(--mobile-bg-primary);
          padding: 16px;
          border-radius: 12px;
          margin-bottom: 12px;
          display: flex;
          justify-content: space-between;
          align-items: center;
          gap: 12px;
        }

        .header-left {
          display: flex;
          align-items: center;
          gap: 12px;
          color: var(--mobile-text-primary);
        }

        .header-icon {
          font-size: 28px;
          color: var(--mobile-bg-primary-card);
        }

        .page-header h1 {
          margin: 0;
          font-size: 20px;
          font-weight: 700;
        }

        .page-header p {
          margin: 4px 0 0 0;
          font-size: 12px;
          opacity: 0.9;
        }

        .balance-mini-card {
          background: linear-gradient(135deg, var(--mobile-bg-primary-card) 0%, #ff8c42 100%);
          padding: 12px 16px;
          border-radius: 10px;
          display: flex;
          align-items: center;
          gap: 10px;
          box-shadow: 0 4px 12px rgba(243, 111, 33, 0.3);
        }

        .balance-icon-mini {
          font-size: 24px;
          color: white;
        }

        .balance-label-mini {
          display: block;
          color: rgba(255,255,255,0.9);
          font-size: 11px;
          font-weight: 500;
        }

        .balance-amount-mini {
          margin: 0;
          color: white;
          font-size: 20px;
          font-weight: 800;
        }

        .next-product-banner {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          padding: 16px;
          border-radius: 12px;
          margin-bottom: 16px;
          display: flex;
          justify-content: space-between;
          align-items: center;
          gap: 12px;
          box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
        }

        .banner-content {
          display: flex;
          align-items: center;
          gap: 12px;
          color: white;
        }

        .banner-icon {
          font-size: 24px;
        }

        .banner-content h4 {
          margin: 0;
          font-size: 14px;
          font-weight: 600;
        }

        .banner-content p {
          margin: 4px 0 0 0;
          font-size: 16px;
          font-weight: 700;
        }

        .btn-quick-buy {
          background: white;
          color: #667eea;
          border: none;
          padding: 10px 20px;
          border-radius: 8px;
          font-size: 14px;
          font-weight: 700;
          cursor: pointer;
          transition: all 0.2s;
          display: flex;
          align-items: center;
          gap: 6px;
        }

        .btn-quick-buy:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(0,0,0,0.2);
        }

        .products-section {
          margin-bottom: 24px;
        }

        .section-title {
          display: flex;
          align-items: center;
          gap: 10px;
          font-size: 16px;
          font-weight: 700;
          color: var(--mobile-text-primary-card);
          margin-bottom: 12px;
        }

        .products-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
          gap: 12px;
        }

        .product-card {
          background: white;
          border-radius: 12px;
          overflow: hidden;
          box-shadow: 0 2px 8px rgba(0,0,0,0.08);
          border: 2px solid var(--mobile-border-light);
          transition: all 0.3s;
          display: flex;
          flex-direction: column;
        }

        .product-card.locked {
          opacity: 0.7;
        }

        .product-card:not(.locked):hover {
          transform: translateY(-4px);
          box-shadow: 0 8px 24px rgba(0,0,0,0.12);
          border-color: var(--mobile-bg-primary-card);
        }

        .product-image {
          position: relative;
          width: 100%;
          height: 160px;
          background: #f5f5f5;
        }

        .product-image img {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }

        .image-placeholder {
          width: 100%;
          height: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 48px;
          color: #ddd;
        }

        .discount-badge {
          position: absolute;
          top: 8px;
          right: 8px;
          background: var(--mobile-bg-primary-card);
          color: white;
          padding: 4px 10px;
          border-radius: 12px;
          font-size: 11px;
          font-weight: 700;
          box-shadow: 0 2px 6px rgba(0,0,0,0.2);
        }

        .label-badge {
          position: absolute;
          top: 8px;
          left: 8px;
          background: #000000;
          color: white;
          padding: 4px 8px;
          border-radius: 10px;
          font-size: 10px;
          font-weight: 600;
          display: flex;
          align-items: center;
          gap: 4px;
        }

        .step-badge {
          position: absolute;
          bottom: 8px;
          left: 8px;
          background: #667eea;
          color: white;
          padding: 4px 12px;
          border-radius: 12px;
          font-size: 11px;
          font-weight: 700;
        }

        .locked-overlay {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0,0,0,0.7);
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          color: white;
          gap: 8px;
        }

        .locked-overlay svg {
          font-size: 28px;
        }

        .locked-overlay span {
          font-size: 12px;
          font-weight: 600;
        }

        .product-info {
          padding: 12px;
          flex: 1;
          display: flex;
          flex-direction: column;
          gap: 6px;
        }

        .product-name {
          margin: 0;
          font-size: 14px;
          font-weight: 600;
          color: #1f2937;
          line-height: 1.3;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .price-row {
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .offer-price {
          font-size: 14px;
          font-weight: 700;
          color: #10b981;
        }

        .original-price {
          font-size: 12px;
          color: #999;
          text-decoration: line-through;
        }

        .lock-message {
          font-size: 10px;
          color: #ef4444;
          font-weight: 600;
          margin-top: 2px;
        }

        .product-actions {
          padding: 12px;
          border-top: 1px solid #e5e7eb;
        }

        .btn-buy-card {
          width: 100%;
          background: var(--mobile-bg-primary-card);
          color: white;
          border: none;
          padding: 10px;
          border-radius: 8px;
          font-size: 13px;
          font-weight: 600;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 6px;
          transition: all 0.2s;
        }

        h5{
        color: var(--mobile-bg-primary)
        }
        .btn-buy-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(243, 111, 33, 0.3);
        }

        .btn-locked-card {
          width: 100%;
          background: #e5e7eb;
          color: #6b7280;
          border: none;
          padding: 10px;
          border-radius: 8px;
          font-size: 13px;
          font-weight: 600;
          cursor: not-allowed;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 6px;
        }

        .instructions-section {
          margin-top: 32px;
        }

        .instructions-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 12px;
        }

        .instruction-card {
          background: white;
          padding: 16px;
          border-radius: 10px;
          display: flex;
          gap: 12px;
          box-shadow: 0 2px 6px rgba(0,0,0,0.06);
          border: 2px solid var(--mobile-border-light);
        }

        .instruction-number {
          width: 32px;
          height: 32px;
          background: var(--mobile-bg-primary-card);
          color: white;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 700;
          font-size: 16px;
          flex-shrink: 0;
        }

        .instruction-content h4 {
          margin: 0 0 4px 0;
          font-size: 13px;
          font-weight: 600;
          color: var( --mobile-text-black);
        }

        .instruction-content p {
          margin: 0;
          font-size: 11px;
          color: #666;
          line-height: 1.4;
        }

        .empty-state {
          text-align: center;
          padding: 48px 20px;
          background: white;
          border-radius: 12px;
        }

        .empty-icon {
          font-size: 56px;
          color: var(--mobile-border-medium);
          margin-bottom: 12px;
        }

        .empty-state p {
          margin: 0;
          font-size: 16px;
          font-weight: 600;
          color: var(--mobile-text-primary-card);
        }

        @media (max-width: 768px) {
          .page-header {
            flex-direction: column;
            align-items: stretch;
          }

          .balance-mini-card {
            justify-content: center;
          }

          .next-product-banner {
            flex-direction: column;
            align-items: stretch;
          }

          .btn-quick-buy {
            width: 100%;
            justify-content: center;
          }

          .instructions-grid {
            grid-template-columns: 1fr;
          }

          .products-grid {
            grid-template-columns: repeat(2, 1fr);
          }
        }
      `}</style>
    </div>
  );
};

export default OfferWallet;
